import { Skeleton } from '@sea-snag/ui';
import { Navigate, useParams } from '@tanstack/react-router';

import { useUser } from '../hooks/use-auth';
import { useCanSeeAdmin } from '../hooks/use-can-see-admin';

interface RequireOwnerOrAdminProps {
  children: React.ReactNode;
}

/**
 * Keeps a member off the management screens even when they type the URL.
 *
 * Modelled on `SuperAdminRoute`: the check happens in render rather than in
 * `beforeLoad`, because the router has no auth in its context — nothing in
 * `beforeLoad` can see the user today.
 *
 * It waits for the session rather than deciding while it loads, or a refresh on
 * an admin screen would bounce the admin out before their role arrived. The API
 * is the real gate (`@OwnerOrAdmin()`); this exists so a member gets a
 * redirect rather than a screen full of failed requests.
 */
export function RequireOwnerOrAdmin({ children }: RequireOwnerOrAdminProps) {
  const { isLoading } = useUser();
  const canSeeAdmin = useCanSeeAdmin();
  const { orgPid } = useParams({ strict: false });

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-full max-w-md" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!canSeeAdmin) {
    return orgPid ? (
      <Navigate to="/$orgPid/dashboard" params={{ orgPid }} replace />
    ) : (
      <Navigate to="/login" replace />
    );
  }

  return <>{children}</>;
}
